import {useState} from 'react'
import {useDialog} from '@/hooks/useDialog'
import {Edit, Trash2, Send, CheckCircle, XCircle, Play, Clock, FileEdit, AlertCircle, Users, Code} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {CommentDialog} from '@/components/shared/CommentDialog'
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '@/components/ui/alert'
import {ApprovalChain} from '@/components/ApprovalChain'
import {EscalationTimelineCard} from '@/components/shared/EscalationTimelineCard'
import {EscalationStepType} from '@/types/escalation'
import {ApprovalStepStatus} from '@/types/access-request'
import {useI18n} from '@/i18n'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {SmallText} from '@/components/shared/SmallText'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {RuleRequestStatus, RuleRequestType, TestResultAction} from '@/types/auto-assign-rule-request'
import type {RuleTest} from '@/types/auto-assign-rule-request'
import {AffectedFilter, useAffectedFilterLabel, useTestResultActionLabel} from '@/features/governance/labels'
import {AccessProfileStatus} from '@/types/access-profile'
import {AutoAssignRuleType} from '@/types/auto-assign-rule'
import {formatTimestamp} from '@/lib/date-utils'
import {Icon} from '@/components/shared/Icon'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {TextLink} from '@/components/shared/TextLink'
import {DetailField} from '@/components/shared/DetailField'
import {IconText} from '@/components/shared/IconText'
import {StatValue} from '@/components/shared/StatValue'
import {StatBox} from '@/components/shared/StatBox'
import {DetailPageGrid} from '@/components/shared/DetailPageGrid'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {EMPTY_SEARCH_AND_FILTERS, type SearchAndFilters} from '@/hooks/use-server-pagination'
import type {SearchAndFilterContract} from '@/types/endpoint-contract'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {useAutoAssignRuleRequest} from '@/features/auto-assign/hooks/useAutoAssignRuleRequest'
import {useAutoAssignRuleRequestPermissions} from '@/features/auto-assign/hooks/useAutoAssignRuleRequestPermissions'
import {useAutoAssignRuleRequestMutations} from '@/features/auto-assign/hooks/useAutoAssignRuleRequestMutations'

const AFFECTED_SEARCH_CONTRACT: SearchAndFilterContract = {
    sort: {},
    filter: {},
    search: {param: 'q'},
}

export function AutoAssignRuleRequestDetail() {
    const i18n = useI18n()
    const affectedFilterLabel = useAffectedFilterLabel()
    const testResultActionLabel = useTestResultActionLabel()
    const {functionGroupTypes} = useAppConfig()
    usePageTitle(i18n.autoAssignRuleRequests.detailPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()

    const deleteDialog = useDialog()
    const submitDialog = useDialog()
    const approveDialog = useDialog()
    const rejectDialog = useDialog()

    const [testResults, setTestResults] = useState<RuleTest | null>(null)
    const [testError, setTestError] = useState<string | null>(null)

    const affectedDialog = useDialog()
    const [affectedFilter, setAffectedFilter] = useState<AffectedFilter>(AffectedFilter.ALL)
    const [affectedSearch, setAffectedSearch] = useState<SearchAndFilters>(EMPTY_SEARCH_AND_FILTERS)

    const {canEdit, canDelete, canSubmit, canTest} = useAutoAssignRuleRequestPermissions()

    const {request, linkedProfiles, linkedGroups, isLoading, error} = useAutoAssignRuleRequest(id)

    const {submit, approve, reject, runTest, deleteRequest} = useAutoAssignRuleRequestMutations(id, {
        onSubmitSuccess: submitDialog.close,
        onApproveSuccess: approveDialog.close,
        onRejectSuccess: rejectDialog.close,
        onTestSuccess: (data) => {
            setTestResults(data)
            setTestError(null)
        },
        onTestError: (err) => {
            setTestError(err.message)
            setTestResults(null)
        },
    })

    const isFunctionGroupRule = request?.ruleType === AutoAssignRuleType.FUNCTION_GROUP

    const getStatusBadge = (status: RuleRequestStatus) => {
        switch (status) {
            case RuleRequestStatus.DRAFT:
                return {variant: BadgeVariant.SECONDARY, icon: FileEdit, label: i18n.autoAssignRuleRequests.draft}
            case RuleRequestStatus.PENDING:
                return {variant: BadgeVariant.OUTLINE, icon: Clock, label: i18n.autoAssignRuleRequests.pendingApproval}
            case RuleRequestStatus.APPROVED:
                return {variant: BadgeVariant.SUCCESS, icon: CheckCircle, label: i18n.autoAssignRuleRequests.approved}
            case RuleRequestStatus.REJECTED:
                return {variant: BadgeVariant.DESTRUCTIVE, icon: XCircle, label: i18n.autoAssignRuleRequests.rejected}
        }
    }

    const getRequestTypeLabel = (requestType: RuleRequestType) => {
        switch (requestType) {
            case RuleRequestType.CREATE:
                return i18n.autoAssignRuleRequests.createNewRule
            case RuleRequestType.UPDATE:
                return i18n.autoAssignRuleRequests.modifyExistingRule
            case RuleRequestType.ACTIVATE:
                return i18n.autoAssignRuleRequests.activateRule
            case RuleRequestType.DEACTIVATE:
                return i18n.autoAssignRuleRequests.deactivateRule
        }
    }

    const isEditable = request?.status === RuleRequestStatus.DRAFT
    const hasApprovalChain = (request?.approvalChain?.length ?? 0) > 0

    const statusBadge = request ? getStatusBadge(request.status) : null
    const StatusIcon = statusBadge ? statusBadge.icon : null

    const handleRunTest = () => {
        if (!request) return
        runTest.mutate({
            conditionExpr: request.conditionExpr,
            profileIds: request.profileIds,
            limit: 50,
        })
    }

    return (
        <PageLayout
            variant="detail"
            title={request?.name ?? i18n.autoAssignRuleRequests.pageTitle}
            icon={CONCEPT_ICON.autoAssignRuleRequest}
            backTo={true}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(request, 'AUTO_ASSIGN_RULE_REQUEST') : undefined)}
            data-testid="auto-assign-rule-request-detail-page"
            description={request && statusBadge && StatusIcon ? (
                <InlineRow>
                    <Badge data-testid={`status-badge-${request.status}`} variant={statusBadge.variant}>
                        <IconText icon={StatusIcon} size="sm" iconClassName="h-3 w-3">
                            {statusBadge.label}
                        </IconText>
                    </Badge>
                    <Badge variant="outline">{getRequestTypeLabel(request.requestType)}</Badge>
                    <Badge variant="secondary">{i18n.autoAssignRules.priority}: {request.priority}</Badge>
                </InlineRow>
            ) : undefined}
            actions={request && statusBadge && StatusIcon ? (
                <InlineRow>
                    {isEditable && canEdit && (
                        <Button data-testid="btn-edit" variant="outline"
                                onClick={() => navigate(routes.accessGovernance.autoAssignRuleRequests.edit(id!))}>
                            <IconText icon={Edit}>{i18n.common.edit}</IconText>
                        </Button>
                    )}
                    {isEditable && canSubmit && (
                        <Button data-testid="btn-submit-for-approval" onClick={submitDialog.open}>
                            <IconText icon={Send}>{i18n.autoAssignRuleRequests.submitForApproval}</IconText>
                        </Button>
                    )}
                    {isEditable && canDelete && (
                        <Button data-testid="btn-delete" variant="ghost" size="icon"
                                onClick={deleteDialog.open}>
                            <Icon icon={Trash2} className="text-destructive"/>
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
            {request && statusBadge && StatusIcon && <>
            <DetailPageGrid
                main={<>
                    <DetailsCard
                        data-testid="card-request-details"
                        title={i18n.autoAssignRuleRequests.requestDetails}
                        description={i18n.autoAssignRuleRequests.requestDetailsDesc}
                        fields={[
                            ...(request.description ? [{label: i18n.common.description, value: request.description, fullWidth: true as const}] : []),
                            {
                                label: i18n.autoAssignRules.owner,
                                value: request.owners.length > 0
                                    ? <TextLink testId="link-owner-party" to={routes.identityManagement.parties.detail(request.owners[0].ownerPartyId)}>{request.owners.map(o => o.ownerPartyDisplayName).join(', ')}</TextLink>
                                    : <MutedText>-</MutedText>,
                            },
                            {label: i18n.autoAssignRules.priority, value: String(request.priority)},
                            {label: i18n.autoAssignRules.removalGraceDays, value: i18n.autoAssignRules.removalGraceDaysValue({days: request.removalGraceDays})},
                            ...(request.userRisk !== null ? [{label: i18n.autoAssignRules.userRiskScore, value: String(request.userRisk)}] : []),
                        ]}
                    />

                    <Card>
                        <CardHeader>
                            <InlineRow justify="between">
                                <div>
                                    <CardTitle data-testid="card-title-expression">
                                        <IconText icon={Code} size="md">{i18n.autoAssignRules.expression}</IconText>
                                    </CardTitle>
                                    <CardDescription>
                                        {isFunctionGroupRule ? i18n.autoAssignRuleRequests.expressionDescFunctionGroup : i18n.autoAssignRuleRequests.expressionDesc}
                                    </CardDescription>
                                </div>
                                {canTest && (
                                    <Button
                                        data-testid="btn-test-rule"
                                        variant="outline"
                                        size="sm"
                                        onClick={handleRunTest}
                                        disabled={runTest.isPending}
                                    >
                                        <IconText icon={Play}>{runTest.isPending ? i18n.autoAssignRules.testing : i18n.autoAssignRules.testRule}</IconText>
                                    </Button>
                                )}
                            </InlineRow>
                        </CardHeader>
                        <CardContent>
                            <CodeBlock>
                                {request.conditionExpr}
                            </CodeBlock>

                            {testError && (
                                <Alert variant="destructive" className="mt-4" data-testid="test-result-error">
                                    <AlertCircle className="h-4 w-4"/>
                                    <AlertTitle>{i18n.autoAssignRules.testFailed}</AlertTitle>
                                    <AlertDescription>{testError}</AlertDescription>
                                </Alert>
                            )}

                            {testResults && (
                                <Stack gap="md" className="mt-4" data-testid="test-result-panel">
                                    <Alert>
                                        <InlineRow justify="between" align="start" className="w-full">
                                            <InlineRow>
                                                <Icon icon={Users} className="mt-0.5"/>
                                                <div>
                                                    <AlertTitle>{i18n.autoAssignRules.testResults}</AlertTitle>
                                                    <AlertDescription>
                                                        {i18n.autoAssignRuleRequests.testCompletedDesc({
                                                            total: testResults.totalEvaluated,
                                                            matched: testResults.matched,
                                                        })}
                                                    </AlertDescription>
                                                </div>
                                            </InlineRow>
                                            {testResults.items && testResults.items.length > 0 && (
                                                <Button
                                                    data-testid="btn-view-affected"
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={affectedDialog.open}
                                                >
                                                    <IconText icon={Users}>{i18n.autoAssignRuleRequests.viewCount({count: testResults.items.length})}</IconText>
                                                </Button>
                                            )}
                                        </InlineRow>
                                    </Alert>

                                    <InlineRow gap="xl" wrap align="stretch">
                                        <StatBox variant="success" className="flex-1">
                                            <StatValue value={testResults.toAdd} variant="success"/>
                                            <MutedText size="xs">{i18n.autoAssignRuleRequests.wouldBeAdded}</MutedText>
                                        </StatBox>
                                        <StatBox variant="danger" className="flex-1">
                                            <StatValue value={testResults.toRemove} variant="danger"/>
                                            <MutedText size="xs">{i18n.autoAssignRuleRequests.wouldBeRemoved}</MutedText>
                                        </StatBox>
                                        <StatBox variant="neutral" className="flex-1">
                                            <StatValue value={testResults.unchanged}/>
                                            <MutedText size="xs">{i18n.autoAssignRuleRequests.unchanged}</MutedText>
                                        </StatBox>
                                    </InlineRow>
                                </Stack>
                            )}
                        </CardContent>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle data-testid="card-title-access-profiles">
                                {isFunctionGroupRule ? i18n.autoAssignRuleRequests.stepFunctionGroups : i18n.autoAssignRules.accessProfiles}
                            </CardTitle>
                            <CardDescription>
                                {isFunctionGroupRule ? i18n.autoAssignRuleRequests.functionGroupsAssignDesc : i18n.autoAssignRuleRequests.profilesAssignDesc}
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {isFunctionGroupRule ? (
                                linkedGroups.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                                <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {linkedGroups.map((functionGroup) => (
                                                <TableRow key={functionGroup.id} data-readonly>
                                                    <TableCell className="font-medium">
                                                        {functionGroup.name}
                                                    </TableCell>
                                                    <TableCell>
                                                        {functionGroup.functionGroupType && (
                                                            <Badge variant="outline">{labelOf(functionGroupTypes, functionGroup.functionGroupType)}</Badge>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <MutedText block centered className="py-4" data-testid="no-function-groups-linked">
                                        {i18n.autoAssignRuleRequests.noFunctionGroupsLinked}
                                    </MutedText>
                                )
                            ) : (
                                linkedProfiles.length > 0 ? (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                                <TableHead data-testid="col-environment">{i18n.accessProfiles.environment}</TableHead>
                                                <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {linkedProfiles.map((profile) => (
                                                <TableRow key={profile.id} data-readonly>
                                                    <TableCell>
                                                        <TextLink
                                                            testId={`link-profile-${profile.id}`}
                                                            to={routes.accessGovernance.profiles.detail(profile.id)}
                                                            variant="bold"
                                                        >
                                                            {profile.name}
                                                        </TextLink>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="secondary">{profile.environment}</Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge
                                                            variant={profile.status === AccessProfileStatus.ACTIVE ? BadgeVariant.SUCCESS : BadgeVariant.SECONDARY}>
                                                            {profile.status}
                                                        </Badge>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <MutedText block centered className="py-4" data-testid="no-profiles-linked">
                                        {i18n.autoAssignRuleRequests.noProfilesLinked}
                                    </MutedText>
                                )
                            )}
                        </CardContent>
                    </Card>

                    {hasApprovalChain && (
                        <Card data-testid="approval-chain-card">
                            <CardHeader>
                                <CardTitle data-testid="card-title-approval-chain">{i18n.approvalChain.title}</CardTitle>
                                {request.matchedPolicyName && (
                                    <CardDescription>
                                        {i18n.autoAssignRuleRequests.matchedPolicy({name: request.matchedPolicyName})}
                                    </CardDescription>
                                )}
                            </CardHeader>
                            <CardContent>
                                <ApprovalChain
                                    steps={request.approvalChain}
                                    onApprove={approveDialog.open}
                                    onReject={rejectDialog.open}
                                    itemId={request.id}
                                    requiredPermission={permissions.autoAssignRules.approve}
                                />
                            </CardContent>
                        </Card>
                    )}
                    {request.approvalChain
                        ?.filter(step => step.status === ApprovalStepStatus.ACTIVE)
                        .map(step => (
                            <EscalationTimelineCard
                                key={`escalation-${step.id}`}
                                stepId={step.id}
                                stepType={EscalationStepType.AUTOASSIGN_REQUEST}
                                escalationLevel={step.escalationLevel}
                                data-testid={`escalation-timeline-${step.id}`}
                            />
                        ))}
                </>}
                sidebar={
                    <Stack gap="lg">
                        <Card>
                            <CardHeader>
                                <CardTitle data-testid="card-title-workflow">{i18n.autoAssignRuleRequests.workflow}</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <Stack gap="md">
                                    <DetailField label={String(i18n.common.createdAt)} value={formatTimestamp(request.createdAt)}/>
                                    <DetailField label={String(i18n.common.lastUpdated)} value={formatTimestamp(request.updatedAt)}/>

                                    {request.reviewedBy && (
                                        <>
                                            <hr/>
                                            <DetailField label={String(i18n.autoAssignRuleRequests.reviewedBy)} value={request.reviewedBy.displayName ?? '-'}/>
                                            {request.reviewedAt && (
                                                <DetailField label={String(i18n.autoAssignRuleRequests.reviewedAt)} value={formatTimestamp(request.reviewedAt)}/>
                                            )}
                                            {request.reviewComment && (
                                                <DetailField label={String(i18n.autoAssignRuleRequests.reviewComment)} value={<SmallText>{request.reviewComment}</SmallText>}/>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                }
            />

            <Dialog open={submitDialog.isOpen} onOpenChange={submitDialog.bind.onOpenChange}>
                <DialogContent data-testid="submit-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.autoAssignRuleRequests.submitForApproval}</DialogTitle>
                        <DialogDescription>
                            {i18n.autoAssignRuleRequests.submitDesc}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="submit"
                        onCancel={submitDialog.close}
                        saving={submit.isPending}
                        saveLabel={i18n.common.submit}
                        savingLabel={i18n.common.submitting}
                        saveType="button"
                        onSave={() => submit.mutate()}
                    />
                </DialogContent>
            </Dialog>

            <CommentDialog
                open={approveDialog.isOpen}
                title={i18n.autoAssignRuleRequests.approveRequest}
                description={i18n.autoAssignRuleRequests.approveDesc}
                commentLabel={i18n.autoAssignRuleRequests.commentOptional}
                commentPlaceholder={i18n.autoAssignRuleRequests.commentPlaceholder}
                commentRequired={false}
                confirmLabel={i18n.common.approve}
                confirmingLabel={i18n.autoAssignRuleRequests.approving}
                confirming={approve.isPending}
                onConfirm={(comment) => approve.mutate({comment: comment.length > 0 ? comment : undefined})}
                onCancel={approveDialog.close}
                data-testid="approve-dialog"
            />

            <CommentDialog
                open={rejectDialog.isOpen}
                title={i18n.autoAssignRuleRequests.rejectRequest}
                description={i18n.autoAssignRuleRequests.rejectDesc}
                commentLabel={i18n.autoAssignRuleRequests.rejectionReason}
                commentPlaceholder={i18n.autoAssignRuleRequests.rejectionReasonPlaceholder}
                commentRequired={true}
                confirmLabel={i18n.common.reject}
                confirmingLabel={i18n.autoAssignRuleRequests.rejecting}
                confirmVariant="destructive"
                confirming={reject.isPending}
                onConfirm={(comment) => reject.mutate({comment})}
                onCancel={rejectDialog.close}
                data-testid="reject-dialog"
            />

            <Dialog open={deleteDialog.isOpen} onOpenChange={deleteDialog.bind.onOpenChange}>
                <DialogContent data-testid="delete-dialog">
                    <DialogHeader>
                        <DialogTitle>{i18n.autoAssignRuleRequests.deleteRequest}</DialogTitle>
                        <DialogDescription>
                            {i18n.autoAssignRuleRequests.deleteConfirm}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="delete"
                        onCancel={deleteDialog.close}
                        saving={deleteRequest.isPending}
                        saveLabel={i18n.common.delete}
                        savingLabel={i18n.common.deleting}
                        destructive
                        saveType="button"
                        onSave={() => deleteRequest.mutate()}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={affectedDialog.isOpen} onOpenChange={affectedDialog.bind.onOpenChange}>
                <DialogContent className="max-w-3xl h-[600px]" data-testid="affected-identities-dialog">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{i18n.autoAssignRules.affectedIdentities}</DialogTitle>
                    </DialogHeader>

                    <InlineRow gap="md" wrap className="flex-shrink-0">
                        {([AffectedFilter.ALL, AffectedFilter.ADD, AffectedFilter.REMOVE, AffectedFilter.UNCHANGED] as const).map((filter) => {
                            const count = filter === AffectedFilter.ALL
                                ? testResults?.items.length ?? 0
                                : testResults?.items.filter(i => i.action === filter).length ?? 0
                            return (
                                <Button
                                    key={filter}
                                    data-testid={`btn-filter-affected-${filter}`}
                                    variant={affectedFilter === filter ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => setAffectedFilter(filter)}
                                >
                                    {affectedFilterLabel(filter)} ({count})
                                </Button>
                            )
                        })}
                    </InlineRow>

                    <UnifiedSearchBar
                        contract={AFFECTED_SEARCH_CONTRACT}
                        value={affectedSearch}
                        onChange={(next) => setAffectedSearch(prev => ({...prev, ...next}))}
                        placeholder={String(i18n.autoAssignRules.searchIdentities)}
                        testIdNamespace="affected-identities"
                    />

                    <ScrollableBox grow className="min-h-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-identity">{i18n.autoAssignRules.identity}</TableHead>
                                    <TableHead data-testid="col-party">{i18n.autoAssignRules.party}</TableHead>
                                    <TableHead data-testid="col-action">{i18n.autoAssignRules.action}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {testResults?.items
                                    .filter(item => affectedFilter === AffectedFilter.ALL || item.action === affectedFilter)
                                    .filter(item =>
                                        !affectedSearch.q ||
                                        item.identityDisplayName.toLowerCase().includes(affectedSearch.q.toLowerCase()) ||
                                        item.partyDisplayName.toLowerCase().includes(affectedSearch.q.toLowerCase()),
                                    )
                                    .map((item) => (
                                        <TableRow key={item.identityId} data-readonly data-testid={`affected-row-${item.identityId}`}>
                                            <TableCell>
                                                <TextLink
                                                    testId={`link-affected-identity-${item.identityId}`}
                                                    to={routes.identityManagement.identities.detail(item.identityId)}
                                                >
                                                    {item.identityDisplayName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                <TextLink
                                                    testId={`link-affected-party-${item.partyId}`}
                                                    to={routes.identityManagement.parties.detail(item.partyId)}
                                                >
                                                    {item.partyDisplayName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant={
                                                        item.action === TestResultAction.ADD ? BadgeVariant.SUCCESS :
                                                            item.action === TestResultAction.REMOVE ? BadgeVariant.DESTRUCTIVE :
                                                                BadgeVariant.SECONDARY
                                                    }
                                                >
                                                    {testResultActionLabel(item.action)}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </ScrollableBox>

                    <MutedText block centered className="pt-2 border-t">
                        {i18n.autoAssignRuleRequests.showingIdentities({
                            filtered: testResults?.items
                                .filter(item => affectedFilter === AffectedFilter.ALL || item.action === affectedFilter)
                                .filter(item =>
                                    !affectedSearch.q ||
                                    item.identityDisplayName.toLowerCase().includes(affectedSearch.q.toLowerCase()) ||
                                    item.partyDisplayName.toLowerCase().includes(affectedSearch.q.toLowerCase()),
                                ).length ?? 0, total: testResults?.items.length ?? 0,
                        })}
                    </MutedText>

                    <DialogFooter>
                        <Button data-testid="btn-close-affected" variant="outline"
                                onClick={affectedDialog.close}>
                            {i18n.common.close}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>}
        </PageLayout>
    )
}
